import DOMPurify from 'dompurify';
import { useTranslation } from 'i18next-vue';

import type { ExcludeFirst, FormatValueFunc, ITableDataResponseColumn } from '@modules/tables/types';
import { useFormatDate } from '@modules/core/composables/datetime';
import { isFunction, isString } from '@utils/guards';
import { transformNumber } from '@utils/numbers';

export interface INewValueResult {
  startPrefix: string;
  value: string;
  endPrefix: string;
}

function prepareNewValue(value: string, startPrefix = '', endPrefix = ''): INewValueResult {
  return ({
    value,
    startPrefix,
    endPrefix,
  });
}

function searchHighlight(value: string, query: string): string {
  return value.replace(
    new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
    '<span class="bg-mark">$&</span>',
  );
}

function computeValue<T>(maybeFunc: string | FormatValueFunc<string> | undefined, value: T, ...args: ExcludeFirst<FormatValueFunc<string>>): string | undefined {
  return isFunction(maybeFunc) ? maybeFunc(value, ...args) : maybeFunc;
}

export default function useTableCellFormatter() {
  const defaultValue = '&ndash;';
  const defaultDateFormat = 'P';
  // eslint-disable-next-line regexp/negation
  const regExpUrl = /(https?:\/\/[^\s]+)/g;
  // eslint-disable-next-line regexp/prefer-plus-quantifier
  const regExpEmail = /^[\w-.]+@([\w-]+\.)+[\w-]{1,}$/g;

  const { t } = useTranslation();
  const { formatDate } = useFormatDate();
  const ALLOWED_TAGS = ['span', 'a'];

  const updateValue = <T>(
    value: T,
    column: ITableDataResponseColumn,
    searchQuery = '',
    rowData?: Record<string, unknown>,
  ): string => purifyHtmlValue(updateValueWithSearchHighlight(getNewValue(value, column, rowData), column, searchQuery), column);

  function purifyHtmlValue(value: string, column: ITableDataResponseColumn): string {
    return DOMPurify.sanitize(value, { ALLOWED_TAGS: [...ALLOWED_TAGS, ...column.columnOptions?.allowedHtmlTags ?? []] });
  }

  function getNewValue<T>(value: T, column: ITableDataResponseColumn, rowData?: Record<string, unknown>): INewValueResult {
    if (column.columnOptions?.formatValue) {
      return prepareNewValue(customFormatValue(value, column, rowData));
    }

    if (value == null) {
      return prepareNewValue(defaultValue);
    }

    if (column.column_type === 'date') {
      return prepareNewValue(formatColumnDate(value, column), customWrapperStart(value, column, rowData), customWrapperEnd(value, column, rowData));
    }

    if (column.column_type === 'datetime') {
      return prepareNewValue(formatColumnDateTime(value, column), customWrapperStart(value, column, rowData), customWrapperEnd(value, column, rowData));
    }

    if (column.money_format) {
      return prepareNewValue(transformNumber(String(value)), customWrapperStart(value, column, rowData), customWrapperEnd(value, column, rowData));
    }

    if (column.column_type === 'string' || column.column_type === 'text') {
      return formatColumnString(value, column, rowData);
    }

    if (column.column_type === 'boolean' && typeof value === 'boolean') {
      return prepareNewValue(formatColumnBoolean(value, column), customWrapperStart(value, column, rowData), customWrapperEnd(value, column, rowData));
    }

    return prepareNewValue(String(value) || defaultValue, customWrapperStart(value, column, rowData), customWrapperEnd(value, column, rowData));
  }

  function customFormatValue<T>(value: T, column: ITableDataResponseColumn, rowData?: Record<string, unknown>): string {
    let val = null;

    if (column.columnOptions?.formatValue) {
      val = column.columnOptions.formatValue(value, column, rowData);
    }
    return val ? String(val) : defaultValue;
  }

  function customWrapperStart<T>(value: T, column: ITableDataResponseColumn, rowData?: Record<string, unknown>): string {
    if (column.columnOptions?.customWrapperStart) {
      return computeValue(column.columnOptions.customWrapperStart, value, column, rowData) || '';
    }

    if (column.columnOptions?.imageSrc) {
      const imgSrc = computeValue(column.columnOptions.imageSrc, value, column, rowData);
      return imgSrc
        ? `
          <span>
            <span class="inline-block align-middle text-center mr-2 ${column.columnOptions?.imageContainerClass || ''}">
              <img class="inline-block" src="${imgSrc}" />
            </span>
        `
        : '';
    }

    return '';
  }

  function customWrapperEnd<T>(value: T, column: ITableDataResponseColumn, rowData?: Record<string, unknown>): string {
    if (column.columnOptions?.customWrapperEnd) {
      return computeValue(column.columnOptions.customWrapperEnd, value, column, rowData) || '';
    }

    if (column.columnOptions?.imageSrc) {
      return `</span>`;
    }

    return '';
  }

  function formatColumnDate<T>(value: T, column: ITableDataResponseColumn): string {
    return formatDate(String(value), column.columnOptions?.dateFormat || defaultDateFormat);
  }

  function formatColumnDateTime<T>(value: T, column: ITableDataResponseColumn): string {
    return formatDate(String(value), column.columnOptions?.dateFormat || `${defaultDateFormat} p`);
  }

  function formatColumnBoolean<T>(value: T, column: ITableDataResponseColumn): string {
    const translationPath = value
      ? column.columnOptions?.localization?.translationPathForTruthyValue
      : column.columnOptions?.localization?.translationPathForFalsyValue;

    return translationPath ? t(translationPath) : String(value);
  }

  function formatColumnString<T>(value: T, column: ITableDataResponseColumn, rowData?: Record<string, unknown>): INewValueResult {
    const path = column.columnOptions?.localization?.translationPath;
    const valuesTranslation = column.columnOptions?.localization?.translationsForValues;

    if (isUrl(value)) {
      return prepareNewValue(
        String(value),
        `${customWrapperStart(value, column, rowData)}<a href="${value}" class="!text-primary hover:no-underline" target="_blank">`,
        `</a>${customWrapperEnd(value, column, rowData)}`,
      );
    }

    if (isEmail(value)) {
      return prepareNewValue(
        String(value),
        `${customWrapperStart(value, column, rowData)}<a href="mailto:${value}" class="!text-primary hover:no-underline">`,
        `</a>${customWrapperEnd(value, column, rowData)}`,
      );
    }

    if (isString(value) && valuesTranslation?.[value]) {
      return prepareNewValue(valuesTranslation[value], customWrapperStart(value, column, rowData), customWrapperEnd(value, column, rowData));
    }

    return prepareNewValue(path ? t(path + value) : String(value), customWrapperStart(value, column, rowData), customWrapperEnd(value, column, rowData));
  }

  function updateValueWithSearchHighlight(
    newValue: INewValueResult,
    column: ITableDataResponseColumn,
    searchQuery: string,
  ): string {
    const value = newValue.value === defaultValue
      ? newValue.value
      : updateValueWithHighlight(newValue.value, searchQuery, column.match);

    return `${newValue.startPrefix}${value}${newValue.endPrefix}`;
  }

  function updateValueWithHighlight<T>(
    value: T,
    query = '',
    isSearchable = true,
  ): T | string {
    return (query && isSearchable ? searchHighlight(String(value), query) : value);
  }

  function isUrl<T>(value: T): boolean {
    return !!(String(value).match(regExpUrl));
  }

  function isEmail<T>(value: T): boolean {
    return !!(String(value).match(regExpEmail));
  }

  return {
    updateValue,
  };
}
